import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { filter, map, takeUntil, timeout } from 'rxjs/operators';

import { Timeout } from '../enums/timeout';

export interface ApiCallConfig {
    cancel$?: Observable<void>;
    timeout?: number;
}

// Matches any generated ng-openapi-gen 1.0.5 function signature: (http, rootUrl, params, context?) => Observable<StrictHttpResponse<R>>
type NgOpenApiFn = (http: HttpClient, rootUrl: string, params: any, context?: HttpContext) => Observable<any>;

// Extracts the **params** type from a generated ng-openapi-gen function.
export type ParamsOf<F extends NgOpenApiFn> = F extends (http: HttpClient, rootUrl: string, params: infer P, ...args: any[]) => any ? Exclude<P, undefined> : never;

// Extracts the **response body** type from a generated ng-openapi-gen function
export type ResponseOf<F extends NgOpenApiFn> = ReturnType<F> extends Observable<infer R> ? R extends { body: infer B } ? B : never : never;

// Wrapped call that returns the response body.
type WrappedCall<F extends NgOpenApiFn> = (params: ParamsOf<F>, config?: ApiCallConfig) => Promise<ResponseOf<F>>;

// Wrapped call that returns the full `HttpResponse`.
type WrappedCall$Response<F extends NgOpenApiFn> = (params: ParamsOf<F>, config?: ApiCallConfig) => Promise<HttpResponse<ResponseOf<F>>>;

/** Marker to tag a function for full-response wrapping inside {@link createApiClient}. */
export interface ResponseMarker<F extends NgOpenApiFn> {
    readonly fn: F;
    readonly withHttpResponse: true;
}

// Resolves a single entry in the function map to its wrapped call type.
type ResolveEntry<V> = V extends ResponseMarker<infer F> ? WrappedCall$Response<F> : V extends NgOpenApiFn ? WrappedCall<V> : never;

// Parse to promise with proper cancellation and timeout
async function toPromise<T>(obs: Observable<T>, config?: ApiCallConfig): Promise<T> {
    if (config?.cancel$)
        obs = obs.pipe(takeUntil(config.cancel$));

    const ms = config?.timeout ?? Timeout.DEFAULT;
    if (ms > 0 && Number.isFinite(ms))
        obs = obs.pipe(timeout(ms));

    return firstValueFrom(obs)
        .catch(err => {
            if (err?.name === 'EmptyError')
                throw new DOMException('The operation was cancelled.', 'AbortError');

            throw err;
        });
}

// Wraps a single generated function, returning the response body.
function wrapBody<F extends NgOpenApiFn>(fn: F, http: HttpClient, rootUrl: string): WrappedCall<F> {
    return (params: ParamsOf<F>, config?: ApiCallConfig) => toPromise(fn(http, rootUrl, params)
        .pipe(
            filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
            map(r => r.body as ResponseOf<F>),
        ),
        config,
    );
}

// Wraps a single generated function, returning the full `HttpResponse`.
function wrapResponse<F extends NgOpenApiFn>(fn: F, http: HttpClient, rootUrl: string): WrappedCall$Response<F> {
    return (params: ParamsOf<F>, config?: ApiCallConfig) =>
        toPromise(fn(http, rootUrl, params).pipe(filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse)) as Observable<HttpResponse<ResponseOf<F>>>, config);
}

/**
 * Tags a generated function so that {@link createApiClient} returns the
 * full `HttpResponse` (status codes, headers) instead of just the body.
 *
 * @example
 * ```ts
 * const api = createApiClient(http, rootUrl, {
 *   delete: withHttpResponse$(deleteSubscription),   // → Promise<HttpResponse<T>>
 *   list:   getSubscriptions,                // → Promise<T>
 * });
 * ```
 */
export function withHttpResponse$<F extends NgOpenApiFn>(fn: F): ResponseMarker<F> {
    return { fn, withHttpResponse: true } as ResponseMarker<F>;
}

/**
 * Creates a typed API client by batch-wrapping generated ng-openapi-gen
 * functions with a shared `HttpClient` + `rootUrl` binding.
 *
 * Each key becomes a method `(params, config?) => Promise<T>`.
 * Tag a function with {@link withHttpResponse$} to get the full `HttpResponse`.
 *
 * **Features:**
 * - True XHR cancellation via `cancel$` (RxJS `takeUntil`)
 * - Automatic timeout of `Timeout.DEFAULT` (20 s), overridable per call
 * - Full type inference from the OpenAPI-generated function signatures
 *
 * @example
 * ```ts
 * @Injectable({ providedIn: 'root' })
 * export class CoordinationApiService {
 *   private readonly _http   = inject(HttpClient);
 *   private readonly _config = inject(ApiConfiguration);
 *
 *   protected readonly _api = createApiClient(this._http, this._config.rootUrl, {
 *     getAll:  organizationCoordinationsGet,
 *     getById: organizationCoordinationGet,
 *     create:  organizationCoordinationPost,
 *     delete:  withHttpResponse$(organizationCoordinationDelete),
 *   });
 * }
 *
 * // Usage:
 * const items = await this._api.getAll({ organizationId }, { cancel$ });
 * const resp  = await this._api.delete({ organizationId, coordinationId });
 * console.log(resp.status);
 * ```
 */
export function createApiClient<T extends Record<string, NgOpenApiFn | ResponseMarker<any>>>(http: HttpClient, rootUrl: string, fns: T,): { [K in keyof T]: ResolveEntry<T[K]> } {
    const client: Record<string, unknown> = {};
    for (const key of Object.keys(fns)) {
        const entry = fns[key];
        if (entry && typeof entry === 'object' && 'withHttpResponse' in entry)
            client[key] = wrapResponse((entry as ResponseMarker<any>).fn, http, rootUrl);
        else
            client[key] = wrapBody(entry as NgOpenApiFn, http, rootUrl);
    }
    return client as { [K in keyof T]: ResolveEntry<T[K]> };
}
